import { AxiosError } from 'axios';
import { DateTime } from 'luxon';
import { queryCache } from 'react-query';

import {
  ACCOUNT_COHORT_MODE_LABEL,
  COHORT_ATTRIBUTE_LABEL,
  COHORT_ERROR_MESSAGE,
  COHORT_HISTORY_QUERY_KEY,
  CSV_REJECTION_REASON_LABEL,
  DEFAULT_EDITION_AUDIENCE,
  EDITION_ACTION_ERROR_MESSAGE,
  EDITION_AUDIENCE_LABEL,
} from './constants';
import {
  CohortErrorCode,
  CohortHistoryDetailFields,
  CohortHistoryEntry,
  CohortHistoryKind,
  CsvImportResult,
  DigestEdition,
  EditionActionErrorCode,
  EditionActionErrorResponse,
  EditionAudience,
  FeatureCatalogEntry,
  FeatureCategory,
  FeatureSubcategory,
} from './types';

const isKnownErrorCode = (code: string): code is EditionActionErrorCode =>
  code in EDITION_ACTION_ERROR_MESSAGE;

const isKnownCohortErrorCode = (code: string): code is CohortErrorCode =>
  code in COHORT_ERROR_MESSAGE;

// Actions on an edition can fail with its own codes or with a cohort's (picking an
// archived cohort, a second open edition for the same cohort...).
export const getEditionActionErrorMessage = (error: unknown, fallback: string): string => {
  const data = (error as AxiosError<EditionActionErrorResponse> | undefined)?.response?.data;
  if (data?.code && isKnownErrorCode(data.code)) {
    return EDITION_ACTION_ERROR_MESSAGE[data.code];
  }
  if (data?.code && isKnownCohortErrorCode(data.code)) {
    return COHORT_ERROR_MESSAGE[data.code];
  }
  return data?.detail || fallback;
};

export const resolveEditionAudience = (audience?: EditionAudience): EditionAudience =>
  audience ?? DEFAULT_EDITION_AUDIENCE;

export const invalidateCohortHistory = (kind: CohortHistoryKind, cohortId: number): void => {
  void queryCache.invalidateQueries([COHORT_HISTORY_QUERY_KEY, kind, cohortId]);
};

export const getCohortErrorMessage = (error: unknown, fallback: string): string => {
  const data = (error as AxiosError<EditionActionErrorResponse> | undefined)?.response?.data;
  if (data?.code && isKnownCohortErrorCode(data.code)) {
    return COHORT_ERROR_MESSAGE[data.code];
  }
  return data?.detail || fallback;
};

// Spreadsheet apps run cells that start with these characters as formulas.
const FORMULA_PREFIX = /^[=+\-@\t\r]/;

const escapeCsvCell = (value: string): string => {
  const safe = FORMULA_PREFIX.test(value) ? `'${value}` : value;
  return /[",\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
};

export const buildRejectedRowsCsv = (rejected: CsvImportResult['rejected']): string => {
  const lines = rejected.map((r) =>
    [
      String(r.row),
      escapeCsvCell(r.email),
      escapeCsvCell(CSV_REJECTION_REASON_LABEL[r.reason]),
    ].join(',')
  );
  return ['fila,email,motivo', ...lines].join('\n');
};

export const downloadCsv = (filename: string, content: string): void => {
  // The BOM makes Excel read the accents as UTF-8.
  const blob = new Blob([`\ufeff${content}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Same key for an edition and for the pill that picks its people cohort: Staff and
// Admins editions carry no cohort row, a custom cohort's editions point at it.
export const baseCohortKey = (audience: EditionAudience): string => `base:${audience}`;

export const customCohortKey = (cohortId: number): string => `custom:${cohortId}`;

export const editionCohortKey = (edition: DigestEdition): string =>
  edition.cohort === null
    ? baseCohortKey(resolveEditionAudience(edition.audience))
    : customCohortKey(edition.cohort);

const pluralize = (count: number, singular: string, plural: string): string =>
  `${count} ${count === 1 ? singular : plural}`;

export const pluralizeAccounts = (count: number): string => pluralize(count, 'cuenta', 'cuentas');

export const pluralizePeople = (count: number): string => pluralize(count, 'persona', 'personas');

export const pluralizeUnsubscribed = (count: number): string => pluralize(count, 'baja', 'bajas');

export const pluralizeExcluded = (count: number): string =>
  pluralize(count, 'excluida', 'excluidas');

// What an edition created before account cohorts carries on itself.
export const describeLegacyAccountFilter = (edition: DigestEdition): string | null => {
  if (edition.cohort_mode === 'all') {
    const excluded = edition.cohort_excluded_accounts.length;
    return excluded > 0
      ? `Todas las cuentas activas (menos ${pluralizeExcluded(excluded)})`
      : 'Todas las cuentas activas';
  }
  if (edition.cohort_mode === 'automatic') {
    const attribute = edition.cohort_attribute;
    return `Por atributo — ${attribute ? COHORT_ATTRIBUTE_LABEL[attribute] : '?'}`;
  }
  if (edition.cohort_mode === 'manual') {
    return `Manual — ${pluralizeAccounts(edition.cohort_accounts.length)}`;
  }
  return null;
};

const HUB_TIME_ZONE = 'America/Puerto_Rico';

export const BEFORE_2026_GROUP_KEY = 'before-2026';
export const BEFORE_2026_GROUP_LABEL = 'Antes de 2026';

const MONTHS_ES = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
];

export type CatalogGroup = {
  key: string;
  label: string;
  entries: FeatureCatalogEntry[];
};

export type CatalogSortOrder = 'newest' | 'oldest';
const monthYearLabel = (moment: DateTime): string =>
  `${MONTHS_ES[moment.month - 1]} ${moment.year}`;

const launchMoment = (entry: FeatureCatalogEntry): DateTime =>
  DateTime.fromISO(entry.launched_at ?? entry.created_at, { zone: HUB_TIME_ZONE });

const byOrder =
  (order: CatalogSortOrder) =>
  (a: FeatureCatalogEntry, b: FeatureCatalogEntry): number => {
    const diff = launchMoment(a).toMillis() - launchMoment(b).toMillis() || a.id - b.id;
    return order === 'newest' ? -diff : diff;
  };

// Entries from before 2026 only know their month: show it next to the "Antes de 2026" mark.
export const launchBadgeLabel = (entry: FeatureCatalogEntry): string | null => {
  if (!entry.launched_before_2026) return null;
  // Only the month is known and it is stored as a midnight-UTC date, so read it in UTC:
  // Puerto Rico time would shift the first of the month back into the previous month.
  const moment = DateTime.fromISO(entry.launched_at ?? entry.created_at, { zone: 'utc' });
  return `${monthYearLabel(moment)} · ${BEFORE_2026_GROUP_LABEL}`;
};

// Month buckets follow Puerto Rico time so a launch late on the last evening of a
// month is not pushed into the next one by UTC. Entries whose exact day is unknown
// (launched_before_2026) never get a month: they share one group that is older than
// every month.
export const groupCatalogEntries = (
  entries: FeatureCatalogEntry[],
  order: CatalogSortOrder = 'newest'
): CatalogGroup[] => {
  const months = new Map<string, CatalogGroup>();
  const beforeTwentyTwentySix: FeatureCatalogEntry[] = [];

  [...entries].sort(byOrder(order)).forEach((entry) => {
    if (entry.launched_before_2026) {
      beforeTwentyTwentySix.push(entry);
      return;
    }
    const moment = launchMoment(entry);
    const key = moment.toFormat('yyyy-MM');
    const group = months.get(key) ?? {
      key,
      label: monthYearLabel(moment),
      entries: [],
    };
    group.entries.push(entry);
    months.set(key, group);
  });

  const sortedMonths = Array.from(months.values()).sort((a, b) =>
    order === 'newest' ? b.key.localeCompare(a.key) : a.key.localeCompare(b.key)
  );
  const beforeGroups: CatalogGroup[] =
    beforeTwentyTwentySix.length > 0
      ? [
          {
            key: BEFORE_2026_GROUP_KEY,
            label: BEFORE_2026_GROUP_LABEL,
            entries: beforeTwentyTwentySix,
          },
        ]
      : [];

  return order === 'newest'
    ? [...sortedMonths, ...beforeGroups]
    : [...beforeGroups, ...sortedMonths];
};

export const NO_CATEGORY_FILTER = 'none';

export type CategoryFilterValue = FeatureCategory | typeof NO_CATEGORY_FILTER;

export const matchesCatalogFilters = (
  entry: FeatureCatalogEntry,
  query: string,
  categories: CategoryFilterValue[],
  tags: string[],
  subcategory: FeatureSubcategory | null = null
): boolean => {
  const q = query.trim().toLowerCase();
  const matchesQuery =
    !q ||
    entry.title.toLowerCase().includes(q) ||
    entry.customer_benefit.toLowerCase().includes(q) ||
    entry.tags.some((tag) => tag.toLowerCase().includes(q));
  const matchesCategory =
    categories.length === 0 || categories.includes(entry.category ?? NO_CATEGORY_FILTER);
  const matchesTags = tags.length === 0 || tags.every((tag) => entry.tags.includes(tag));
  const matchesSubcategory = subcategory === null || entry.subcategory === subcategory;
  return matchesQuery && matchesCategory && matchesTags && matchesSubcategory;
};

const collectMessages = (value: unknown): string[] => {
  if (typeof value === 'string') return [value];
  if (Array.isArray(value)) return value.flatMap(collectMessages);
  if (value && typeof value === 'object') return Object.values(value).flatMap(collectMessages);
  return [];
};

// DRF answers 400 with { field: ['message'] } (nested for list fields like tags);
// flatten each field to one displayable string.
export const getFeatureFieldErrors = (error: unknown): Record<string, string> => {
  const data = (error as AxiosError<unknown> | undefined)?.response?.data;
  if (!data || typeof data !== 'object') return {};
  return Object.entries(data).reduce<Record<string, string>>((acc, [field, value]) => {
    const message = collectMessages(value).join(' ');
    return message ? { ...acc, [field]: message } : acc;
  }, {});
};

const HEAD_OPEN_TAG = /<head(\s[^>]*)?>/i;
const BASE_TAG = /<base[\s/>]/i;
const DOCTYPE = /^\s*<!doctype[^>]*>/i;
const OPEN_IN_NEW_TAB_BASE = '<base target="_blank">';

// Email links carry no target, so inside the sandboxed preview they would navigate the iframe.
export const openPreviewLinksInNewTab = (html: string): string => {
  if (BASE_TAG.test(html)) {
    return html;
  }
  if (HEAD_OPEN_TAG.test(html)) {
    return html.replace(HEAD_OPEN_TAG, (headTag) => `${headTag}${OPEN_IN_NEW_TAB_BASE}`);
  }
  const doctype = DOCTYPE.exec(html)?.[0] ?? '';
  return `${doctype}${OPEN_IN_NEW_TAB_BASE}${html.slice(doctype.length)}`;
};

export const formatCohortHistoryDate = (isoDate: string): string =>
  DateTime.fromISO(isoDate, { zone: HUB_TIME_ZONE }).toLocaleString(DateTime.DATETIME_MED);

export const getCohortHistoryActorLabel = (entry: CohortHistoryEntry): string => {
  if (entry.source === 'system') return 'Sistema';
  return entry.actor?.name ?? 'Usuario eliminado';
};

const describeHistoryFilter = ({ mode, attribute }: CohortHistoryDetailFields): string => {
  if (!mode) return '?';
  if (mode === 'automatic' && attribute) {
    return `${ACCOUNT_COHORT_MODE_LABEL[mode]} (${COHORT_ATTRIBUTE_LABEL[attribute]})`;
  }
  return ACCOUNT_COHORT_MODE_LABEL[mode];
};

const describeHistoryUser = ({ subject_user }: CohortHistoryEntry): string =>
  subject_user ? `${subject_user.name} (${subject_user.email})` : 'un usuario eliminado';

const describeHistoryAccount = ({ subject_account }: CohortHistoryEntry): string =>
  subject_account ? `la cuenta ${subject_account.name}` : 'una cuenta eliminada';

const describeHistorySendList = (fields?: CohortHistoryDetailFields): string =>
  fields?.send_list ? EDITION_AUDIENCE_LABEL[fields.send_list] : '?';

export const describeCohortHistoryChange = (entry: CohortHistoryEntry): string => {
  const { old: before = {}, new: after = {} } = entry.details;
  switch (entry.action) {
    case 'cohort_created':
      return 'creó el cohort';
    case 'cohort_renamed':
      return `renombró el cohort de «${before.name ?? '?'}» a «${after.name ?? '?'}»`;
    case 'cohort_send_list_changed':
      return `cambió la lista de envío de ${describeHistorySendList(
        before
      )} a ${describeHistorySendList(after)}`;
    case 'cohort_archived':
      return 'archivó el cohort';
    case 'member_added':
      return `agregó a ${describeHistoryUser(entry)}`;
    case 'member_removed':
      return `quitó a ${describeHistoryUser(entry)}`;
    case 'account_added':
      return `agregó ${describeHistoryAccount(entry)}`;
    case 'account_removed':
      return `quitó ${describeHistoryAccount(entry)}`;
    case 'account_excluded':
      return `excluyó ${describeHistoryAccount(entry)}`;
    case 'account_unexcluded':
      return `volvió a incluir ${describeHistoryAccount(entry)}`;
    case 'cohort_filter_changed':
      return `cambió el filtro de ${describeHistoryFilter(before)} a ${describeHistoryFilter(
        after
      )}`;
    default:
      return 'hizo un cambio';
  }
};
